import { FC } from 'react';
import { AFI_THRESHOLDS } from '@leopart/calc';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import dayjs from 'dayjs';
import { ChartEmptyState } from '../AthleteInjuryCharts/charts/_shared/ChartEmptyState';
import styles from './AFITrendChart.module.scss';

interface AFITrendChartProps {
  data: Array<{ date: string; score: number }>;
  height?: number;
}

function getAFIClass(score: number): string {
  // Soglie centralizzate in @leopart/calc (AFI_THRESHOLDS).
  if (score >= AFI_THRESHOLDS.antifragile) return 'Antifragile';
  if (score >= AFI_THRESHOLDS.resilient) return 'Resiliente';
  if (score >= AFI_THRESHOLDS.fragile) return 'Fragile';
  return 'Vulnerabile';
}

export const AFITrendChart: FC<AFITrendChartProps> = ({ data, height = 220 }) => {
  if (!data || data.length === 0) {
    return <ChartEmptyState />;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>Trend AFI — Ultimi 90 giorni</div>
      <div className={styles.legend}>
        <span className={styles.legendItemAntifragile}>
          <span className={styles.legendLine} style={{ borderTop: '2px solid #34a868' }} />
          Antifragile (≥75)
        </span>
        <span className={styles.legendItemResilient}>
          <span className={styles.legendLineDashed} style={{ borderTop: '2px dashed #38bdf8' }} />
          Resiliente (≥55)
        </span>
        <span className={styles.legendItemFragile}>
          <span className={styles.legendLineDashed} style={{ borderTop: '2px dashed #fbbf24' }} />
          Fragile (≥35)
        </span>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e7" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(v) => dayjs(v).format('DD/MM')}
            tick={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi', sans-serif" }}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fill: '#4d4d4d', fontSize: 11, fontFamily: "'Satoshi', sans-serif" }}
          />
          <Tooltip
            formatter={(value) => [value, getAFIClass(Number(value))]}
            labelFormatter={(label) => dayjs(label).format('DD/MM/YYYY')}
          />
          <ReferenceLine y={75} stroke="#34a868" strokeWidth={1.5} />
          <ReferenceLine y={55} stroke="#38bdf8" strokeDasharray="4 3" strokeWidth={1.5} />
          <ReferenceLine y={35} stroke="#fbbf24" strokeDasharray="4 3" strokeWidth={1.5} />
          <Line
            type="monotone"
            dataKey="score"
            stroke="#5BC9E0"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
